import { useAuth } from "@src/contexts/AuthProvider";
import { ApiCallParams, sudoziFetchBase } from "@src/infrastructure/apiCall";
import {
  PublicQueryRoute,
  QueryRoute,
} from "@src/sudozi-schemas/api-route-schemas";
import { useEffect } from "react";
import {
  QueryKey,
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQuery,
} from "react-query";
import { z } from "zod";

function mergeQueryKeys(queryKey: QueryKey, ...keys: string[]) {
  return typeof queryKey === "string"
    ? [queryKey, ...keys]
    : [...queryKey, ...keys];
}

export function useSudoziPublicQuery<
  R extends PublicQueryRoute,
  O extends R["output"] = R["output"],
  U extends Parameters<typeof useQuery<z.infer<O>>> = Parameters<
    typeof useQuery<z.infer<O>>
  >,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const outputSchema = route.output;

  return useQuery(
    queryKey,
    async () => {
      const result = await sudoziFetchBase({
        route,
        params,
        query,
      });

      return outputSchema.parse(await result.json());
    },
    options,
  );
}

export function useSudoziQuery<
  R extends QueryRoute,
  O extends R["output"] = R["output"],
  U extends Parameters<typeof useQuery<z.infer<O>>> = Parameters<
    typeof useQuery<z.infer<O>>
  >,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const { tokenUserData } = useAuth();
  const outputSchema = route.output;

  return useQuery(
    mergeQueryKeys(queryKey, tokenUserData?.userId ?? ""),
    async () => {
      if (!tokenUserData) {
        return null;
      }
      const result = await sudoziFetchBase({
        route,
        params,
        query,
      });

      return outputSchema.parse(await result.json());
    },
    options,
  );
}

export function useSudoziVendorQuery<
  R extends QueryRoute,
  O extends R["output"] = R["output"],
  U extends Parameters<typeof useQuery<z.infer<O>>> = Parameters<
    typeof useQuery<z.infer<O>>
  >,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const { vendorUser } = useAuth();
  const outputSchema = route.output;

  return useQuery(
    mergeQueryKeys(queryKey, vendorUser?.userId ?? ""),
    async () => {
      if (!vendorUser) {
        console.warn("No valid vendor portal token found to make request");
        return null;
      }
      const result = await sudoziFetchBase({
        route,
        params,
        query,
      });

      return outputSchema.parse(await result.json());
    },
    options,
  );
}

export function useSudoziInfinitePublicQuery<
  R extends PublicQueryRoute,
  O extends R["output"],
  U extends Parameters<typeof useInfiniteQuery<z.infer<O>>>,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const outputSchema = route.output;

  return useInfiniteQuery<z.infer<O>>(
    queryKey,
    async ({ pageParam }: { pageParam?: string }) => {
      const result = await sudoziFetchBase({
        route,
        params,
        query: !pageParam
          ? query
          : {
              ...query,
              cursor: pageParam,
            },
      });

      return outputSchema.parse(await result.json());
    },
    {
      ...options,
      getNextPageParam: (lastPage) => {
        return lastPage?.pageInfo.hasNextPage
          ? lastPage.pageInfo.endCursor
          : undefined;
      },
    },
  );
}

export function useSudoziInfiniteQuery<
  R extends QueryRoute,
  O extends R["output"],
  U extends Parameters<typeof useInfiniteQuery<z.infer<O>>>,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const { tokenUserData } = useAuth();
  const outputSchema = route.output;

  return useInfiniteQuery<z.infer<O>>(
    mergeQueryKeys(queryKey, tokenUserData?.userId ?? ""),
    async ({ pageParam }: { pageParam?: string }) => {
      if (!tokenUserData) {
        console.warn("No valid company token found to make request");
        return {
          pageInfo: {
            hasNextPage: false,
            endCursor: null,
          },
          edges: [],
        };
      }
      const result = await sudoziFetchBase({
        route,
        params,
        query: !pageParam
          ? query
          : {
              ...query,
              cursor: pageParam,
            },
      });

      return outputSchema.parse(await result.json());
    },
    {
      ...options,
      getNextPageParam: (lastPage) => {
        return lastPage?.pageInfo.hasNextPage
          ? lastPage.pageInfo.endCursor
          : undefined;
      },
    },
  );
}

export function useSudoziVendorInfiniteQuery<
  R extends QueryRoute,
  O extends R["output"],
  U extends Parameters<typeof useInfiniteQuery<z.infer<O>>>,
>({
  route,
  queryKey,
  options,
  params,
  query,
}: ApiCallParams<R> & {
  queryKey: U[0];
  options?: U[2];
}) {
  const { vendorUser } = useAuth();
  const outputSchema = route.output;

  return useInfiniteQuery<z.infer<O>>(
    mergeQueryKeys(queryKey, vendorUser?.userId ?? ""),
    async ({ pageParam }: { pageParam?: string }) => {
      if (!vendorUser) {
        console.warn("No valid vendor portal token found to make request");
        return {
          pageInfo: {
            hasNextPage: false,
            endCursor: null,
          },
          edges: [],
        };
      }
      const result = await sudoziFetchBase({
        route,
        params,
        query: !pageParam
          ? query
          : {
              ...query,
              cursor: pageParam,
            },
      });

      return outputSchema.parse(await result.json());
    },
    {
      ...options,
      getNextPageParam: (lastPage) => {
        return lastPage?.pageInfo.hasNextPage
          ? lastPage.pageInfo.endCursor
          : undefined;
      },
    },
  );
}

/**
 * This hook is used to automatically exhaust an infinite query.
 * It will continue fetching the next page of data until there is no more data to fetch.
 *
 * @param infiniteQueryResult The result of an infinite query
 * @example
 * const { data } = useAutiInfiniteQuery(
 *  useSudoziInfiniteQuery({...})
 * )
 */
export function useAutoInfiniteQuery<
  TData,
  TInfiniteQueryResult extends UseInfiniteQueryResult<TData>,
>(infiniteQueryResult: TInfiniteQueryResult) {
  useEffect(() => {
    if (infiniteQueryResult.data && infiniteQueryResult.hasNextPage) {
      void infiniteQueryResult.fetchNextPage();
    }
  }, [infiniteQueryResult]);

  return infiniteQueryResult;
}
